import { ComponentStore, tapResponse } from '@ngrx/component-store';
import { Injectable, inject } from '@angular/core';
import { exhaustMap, Observable, pipe, tap, filter } from 'rxjs';
import { IUser, UserPlan } from '../interfaces';
import { Router } from '@angular/router';
import { SettingsService } from '../shared/services/api/settings.service';
import { ErrorHandlerService } from '../shared/services/error-handler.service';
import { NzMessageService } from 'ng-zorro-antd/message';
import { GuestAccessStore } from './guest-store';
import { SaveLocalService } from '../pages/auth/services/save-local.service';
import { TokenService } from '../pages/auth/services/token.service';
import { HttpErrorResponse } from '@angular/common/http';
import {
  ProjectFromList,
  SubscriptionType,
} from '../pages/workspace/features/create-workspace/services/workspace-api.service';
import { PdfExportContextService } from '../core/services/pdf-export-context.service';

export type SelectedWorkspaceState = {
  workspace: ProjectFromList | null;
  user: IUser | null;
  guestUser: IUser | null;
};

export type RenewState = { isExpireApplicable: boolean, daysTillExpire: number }

export type UpdateUserPayload = {
  name: string;
  company: string;
  role: string;
  avatar?: string;
  locale?: string;
  phone: string;
};

export const isUserProfileIncomplete = (user: IUser | null | undefined): boolean => {
  if (!user) {
    return true;
  }

  const hasName = !!user.name && user.name.trim().length > 0;
  const hasPhone = !!user.phone && user.phone.trim().length > 0 && user.phone.trim() !== '967870890';
  const hasCompany = !!user.company && user.company.trim().length > 0;
  const hasRole = !!user.role && user.role.trim().length > 0;

  return !(hasName && hasPhone && hasCompany && hasRole);
};

@Injectable({
  providedIn: 'root',
})
export class SelectedWorkspaceStore extends ComponentStore<SelectedWorkspaceState> {
  public project$ = this.select((state) => state.workspace);
  public user$: Observable<IUser | null> = this.select(
    GuestAccessStore.state$,
    this.state$,
    (userAccessState, state) => {
      if (userAccessState.enabled) {
        return userAccessState.user;
      }

      return state.user;
    },
  );

  public storeState$ = this.select({
    project: this.project$,
    user: this.user$,
  });

  setWorkspace = this.updater((state, workspace: ProjectFromList | null) => ({
    ...state,
    workspace: workspace,
  }));

  public setUser = this.updater((state, user: IUser) => {
    return { ...state, user: user };
  });

  private pdfContext = inject(PdfExportContextService);

  constructor(
    private local: SaveLocalService,
    private settings: SettingsService,
    private router: Router,
    private errorHandler: ErrorHandlerService,
    private message: NzMessageService,
    private tokenService: TokenService,
  ) {
    super({ workspace: null, user: null, guestUser: null });

    this.init();
  }

  private init() {
    const savedUser = this.local.getUser();

    if (savedUser) {
      this.setUser(savedUser);
    }
  }

  getStateSync(): SelectedWorkspaceState {
    return this.get();
  }

  public setCurrentWorkspace = this.effect((company$: Observable<ProjectFromList>) => {
    return company$.pipe(
      tap((project) => {
        if (!project) {
          return;
        }

        if (!project.id) {
          return;
        }

        this.setWorkspace(project);
      })
    );
  });

  refreshUser = this.effect<void>(
    pipe(
      filter(() => {
        // Skip in data-only mode (check both context and URL directly)
        const isDataOnlyRoute = typeof window !== 'undefined' && 
          (window.location.href.includes('/dashboard-export-data/') || 
           this.router.url.includes('/dashboard-export-data/'));
        return this.hasActiveAuthToken() && !this.pdfContext.isDataOnlyMode && !isDataOnlyRoute;
      }),
      exhaustMap(() => {
        return this.settings.infoUser().pipe(
          tapResponse(
            (user: IUser) => {
              this.setUser(user);
              this.local.setUser(user);
            },
            (err) => {
              if (err instanceof HttpErrorResponse && err.status === 401) {
                return;
              }
              console.error(err);
            },
          ),
        );
      }),
    ),
  );

  public updateUserData = this.effect<UpdateUserPayload>((args$) => {
    return args$.pipe(
      exhaustMap((args) => {
        return this.settings.updateUser(args).pipe(
          tapResponse(
            (success) => {
              this.refreshUser();
              this.message.success('User info updated');
            },
            (err) => {
              this.errorHandler.handleError(err);
            },
          ),
        );
      }),
    );
  });

  public getUserSync() {
    if (GuestAccessStore.getState().enabled) {
      return GuestAccessStore.getState().user;
    }

    return this.get().user;
  }

  public clearState() {
    this.setState({ workspace: null, user: null, guestUser: null });
  }

  private hasActiveAuthToken(): boolean {
    if (this.tokenService.checkToken()) {
      return true;
    }

    const guestState = GuestAccessStore.getState();
    return !!guestState?.enabled && !!guestState?.token;
  }
}

export const hasAccessByLevel = (userLevel: UserPlan, requiredLevel: UserPlan) => {
  const accessLevels = ['guest', 'free', 'standart', 'professional', 'enterprice'];

  const userIndex = accessLevels.indexOf(userLevel);
  const requiredIndex = accessLevels.indexOf(requiredLevel);

  if (userIndex === -1 || requiredIndex === -1) {
    return false;
  }

  return userIndex >= requiredIndex;
};
